import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SpecDocMeta } from '@bom/shared';
import { api } from '../lib/apiClient';

/** 规格书列表（跨产品共享：全平台规格书 ∩ 本产品 BOM 料号，时间倒序） */
export function useSpecs(tid: string | undefined) {
  return useQuery({
    queryKey: ['specs', tid],
    queryFn: () => api.get<{ specs: SpecDocMeta[] }>(`/tenants/${tid}/specs`).then((r) => r.specs),
    enabled: !!tid,
  });
}

/** 某料号的规格书更新时间线 */
export function useSpecTimeline(tid: string | undefined, partNo: string | undefined) {
  return useQuery({
    queryKey: ['spec-timeline', tid, partNo],
    queryFn: () =>
      api
        .get<{ timeline: SpecDocMeta[] }>(`/tenants/${tid}/specs/timeline`, {
          query: { part_no: partNo as string },
        })
        .then((r) => r.timeline),
    enabled: !!tid && !!partNo,
  });
}

/**
 * 下载规格书：按规格书记录签发（文件可能存于来源产品的附件前缀下，
 * 跨产品共享后不能走 openAttachment 的 tenant prefix 检查）
 */
export async function openSpecFile(tid: string, id: string, partNo: string): Promise<void> {
  const r = await api.get<{ download_url: string }>(
    `/tenants/${tid}/specs/${encodeURIComponent(id)}/download`,
    { query: { part_no: partNo } },
  );
  window.open(r.download_url, '_blank', 'noopener');
}

interface CreateSpecBody {
  part_nos: string[]; // 归属料号（可多个共用同一份文件）
  filename: string;
  s3_key: string;
  note?: string;
  size_bytes?: number;
}

export function useCreateSpec(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSpecBody) => api.post<{ id: string }>(`/tenants/${tid}/specs`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['specs', tid] });
      qc.invalidateQueries({ queryKey: ['spec-timeline', tid] });
    },
  });
}

export function useDeleteSpec(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; part_no: string }) =>
      api.del<{ deleted: number }>(`/tenants/${tid}/specs/${encodeURIComponent(vars.id)}`, {
        query: { part_no: vars.part_no },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['specs', tid] });
      qc.invalidateQueries({ queryKey: ['spec-timeline', tid] });
    },
  });
}
